import React, { useRef, useEffect, useState } from 'react';

interface VideoHeroProps {
  webmUrl?: string;
  av1Url?: string;
  fallbackImageUrl?: string;
}

const VideoHero: React.FC<VideoHeroProps> = ({
  webmUrl = "/hero-loop.webm",
  av1Url = "/hero-loop-av1.webm",
  fallbackImageUrl = "/hero-fallback.webp"
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
  const [isReducedMotion, setIsReducedMotion] = useState(false);

  useEffect(() => {
    // 1. Check prefers-reduced-motion and save-data
    if (typeof window !== 'undefined') {
      const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (motionQuery.matches) {
        setIsReducedMotion(true);
        return;
      }

      const connection = (navigator as any).connection;
      if (connection && (connection.saveData || connection.effectiveType === '2g' || connection.effectiveType === 'slow-2g')) {
        setIsReducedMotion(true);
        return;
      }

      // 2. Defer video loading after initial paint and LCP
      const startVideoLoad = () => {
        setShouldLoadVideo(true);
      };

      if ('requestIdleCallback' in window) {
        (window as any).requestIdleCallback(startVideoLoad, { timeout: 2500 });
      } else {
        const timer = setTimeout(startVideoLoad, 1000);
        return () => clearTimeout(timer);
      }
    }
  }, []);

  useEffect(() => {
    // 3. Pause video when document is hidden to conserve resources
    const handleVisibilityChange = () => {
      if (videoRef.current) {
        if (document.hidden) {
          videoRef.current.pause();
        } else {
          videoRef.current.play().catch(() => {});
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return (
    <div className="absolute inset-0 z-0 h-full w-full overflow-hidden bg-[#030811]">
      {/* 1. LCP Hero Poster Image (Always rendered in initial SSR HTML with High Priority) */}
      <picture>
        <source srcSet={fallbackImageUrl} type="image/webp" />
        <img
          src={fallbackImageUrl}
          alt="WE24 Agency Cinema Reel Backdrop"
          fetchPriority="high"
          decoding="async"
          className={`absolute inset-0 h-full w-full object-cover filter brightness-[0.88] contrast-[1.05] transition-opacity duration-1000 ${
            videoLoaded ? 'opacity-0 pointer-events-none' : 'opacity-100'
          }`}
        />
      </picture>

      {/* 2. Lazy Mounted Cinema Loop Video (Mounted post-LCP when motion is permitted) */}
      {shouldLoadVideo && !isReducedMotion && (
        <video
          ref={videoRef}
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          onCanPlay={() => setVideoLoaded(true)}
          onError={() => setVideoLoaded(false)}
          aria-hidden="true"
          className={`absolute inset-0 z-0 h-full w-full object-cover hero-video filter brightness-[0.92] contrast-[1.04] transition-opacity duration-1000 ${
            videoLoaded ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {av1Url && <source src={av1Url} type="video/webm; codecs=av01.0.05M.08" />}
          {webmUrl && <source src={webmUrl} type="video/webm" />}
        </video>
      )}

      {/* Light full-bleed overlay allowing sea & landscape colors to show clearly */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/15 to-[#05131f]/40 backdrop-blur-[0.5px] z-[1]" />

      {/* Top feather for smooth navbar transition */}
      <div className="absolute top-0 inset-x-0 h-24 bg-gradient-to-b from-[#05131f]/80 to-transparent pointer-events-none z-[2]" />

      {/* Gentle bottom feather right at the base so sea remains visible */}
      <div className="absolute bottom-0 inset-x-0 h-28 bg-gradient-to-t from-[#05131f] via-[#05131f]/60 to-transparent pointer-events-none z-[2]" />
    </div>
  );
};

export default VideoHero;
